/**
 * C02 §6 的 WebSocket client。
 *
 * 契約要求（逐條對應到實作）：
 *  §6    一條連線可訂閱多個頻道 → `subscribe(channel)`，內部保存 channel 集合。
 *  §6.2  伺服器每 20s 送 ping、60s 無 pong 視為斷線 → 我們收到 `{"op":"ping"}` 立刻回
 *        `{"op":"pong"}`；同時自己也每 20s 送一次 ping，並在 60s 沒有任何訊息時主動重連。
 *  §6.3  斷線重連後送 `{"op":"resync","match_id":...}`。
 *        **斷線不等於棄權** → 這個 client 不會因為斷線就送 forfeit，也不會清掉本地狀態。
 *
 * 設計上刻意把 socket 的建立抽成 `socketFactory`，讓 MSW / 測試可以塞假的 socket 進來，
 * UI 完全不需要知道差別。
 */

import type { WsClientMessage, WsServerMessage } from './types';

export interface SocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}

export type SocketFactory = (url: string) => SocketLike;

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface GameSocketOptions {
  url: string;
  getToken: () => string | null;
  socketFactory?: SocketFactory;
  /** 測試時關掉，避免 fake timer 打架。 */
  heartbeat?: boolean;
  heartbeatIntervalMs?: number;
  staleTimeoutMs?: number;
  maxBackoffMs?: number;
}

export interface GameSocketHandlers {
  onMessage(msg: WsServerMessage): void;
  onStateChange(state: ConnectionState, attempt: number): void;
}

const DEFAULT_HEARTBEAT_MS = 20_000;
const DEFAULT_STALE_MS = 60_000;
const DEFAULT_MAX_BACKOFF_MS = 15_000;

export class GameSocket {
  private readonly opts: Required<Omit<GameSocketOptions, 'socketFactory' | 'getToken' | 'url'>> &
    Pick<GameSocketOptions, 'socketFactory' | 'getToken' | 'url'>;
  private readonly handlers: GameSocketHandlers;

  private socket: SocketLike | null = null;
  private state: ConnectionState = 'idle';
  private attempt = 0;
  private closedByUs = false;

  private channels = new Set<string>();
  /** 需要在重連後 resync 的對局。 */
  private matches = new Set<string>();

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private lastMessageAt = 0;

  constructor(options: GameSocketOptions, handlers: GameSocketHandlers) {
    this.opts = {
      url: options.url,
      getToken: options.getToken,
      socketFactory: options.socketFactory,
      heartbeat: options.heartbeat ?? true,
      heartbeatIntervalMs: options.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_MS,
      staleTimeoutMs: options.staleTimeoutMs ?? DEFAULT_STALE_MS,
      maxBackoffMs: options.maxBackoffMs ?? DEFAULT_MAX_BACKOFF_MS,
    };
    this.handlers = handlers;
  }

  get connectionState(): ConnectionState {
    return this.state;
  }

  connect(): void {
    if (this.state === 'open' || this.state === 'connecting') return;
    this.closedByUs = false;
    this.openSocket();
  }

  private setState(next: ConnectionState): void {
    if (this.state === next) return;
    this.state = next;
    this.handlers.onStateChange(next, this.attempt);
  }

  private buildUrl(): string {
    const token = this.opts.getToken();
    // C02 §6：`wss://.../ws?token=<jwt>`
    if (!token) return this.opts.url;
    const sep = this.opts.url.includes('?') ? '&' : '?';
    return `${this.opts.url}${sep}token=${encodeURIComponent(token)}`;
  }

  private openSocket(): void {
    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const factory: SocketFactory =
      this.opts.socketFactory ?? ((url: string) => new WebSocket(url) as unknown as SocketLike);

    let sock: SocketLike;
    try {
      sock = factory(this.buildUrl());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = sock;

    sock.onopen = () => {
      this.attempt = 0;
      this.lastMessageAt = Date.now();
      this.setState('open');
      // 重新訂閱所有頻道，並對每一場對局要求 resync（C02 §6.3）。
      for (const ch of this.channels) this.send({ op: 'subscribe', channel: ch });
      for (const m of this.matches) this.send({ op: 'resync', match_id: m });
      this.startHeartbeat();
    };

    sock.onmessage = (ev) => {
      this.lastMessageAt = Date.now();
      const parsed = parseServerMessage(ev.data);
      if (!parsed) return;
      // 心跳自動回應：伺服器 60s 收不到 pong 會把我們踢掉（C02 §6.2）。
      if (parsed.op === 'ping') {
        this.send({ op: 'pong' });
        return;
      }
      this.handlers.onMessage(parsed);
    };

    sock.onerror = () => {
      /* onclose 一定會跟著來，重連邏輯統一放那裡 */
    };

    sock.onclose = () => {
      this.stopHeartbeat();
      this.socket = null;
      if (this.closedByUs) {
        this.setState('closed');
        return;
      }
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    this.setState('reconnecting');
    this.attempt += 1;
    const base = Math.min(500 * 2 ** (this.attempt - 1), this.opts.maxBackoffMs);
    // jitter：避免整個大廳的人在同一毫秒一起回來把後端打爆
    const delay = base * (0.5 + Math.random() * 0.5);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => this.openSocket(), delay);
  }

  private startHeartbeat(): void {
    if (!this.opts.heartbeat) return;
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ op: 'ping' });
      if (Date.now() - this.lastMessageAt > this.opts.staleTimeoutMs) {
        // 連線看起來活著但其實已經半開（手機切網最常見）→ 主動打掉重來。
        this.forceReconnect();
      }
    }, this.opts.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  forceReconnect(): void {
    try {
      this.socket?.close(4000, 'client forced reconnect');
    } catch {
      /* ignore */
    }
    this.socket = null;
    this.stopHeartbeat();
    this.scheduleReconnect();
  }

  subscribe(channel: string): void {
    this.channels.add(channel);
    if (this.state === 'open') this.send({ op: 'subscribe', channel });
  }

  /** 追蹤一場對局：重連時會自動 resync。 */
  trackMatch(matchId: string): void {
    this.matches.add(matchId);
    this.subscribe(`match:${matchId}`);
  }

  untrackMatch(matchId: string): void {
    this.matches.delete(matchId);
    this.channels.delete(`match:${matchId}`);
  }

  resync(matchId: string): void {
    this.send({ op: 'resync', match_id: matchId });
  }

  send(msg: WsClientMessage): boolean {
    const sock = this.socket;
    if (!sock || sock.readyState !== 1) return false;
    try {
      sock.send(JSON.stringify(msg));
      return true;
    } catch {
      return false;
    }
  }

  close(): void {
    this.closedByUs = true;
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    try {
      this.socket?.close(1000, 'client closed');
    } catch {
      /* ignore */
    }
    this.socket = null;
    this.setState('closed');
  }
}

export function parseServerMessage(data: unknown): WsServerMessage | null {
  if (typeof data !== 'string') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const op = (parsed as { op?: unknown }).op;
  if (op !== 'event' && op !== 'error' && op !== 'pong' && op !== 'ping' && op !== 'sync') return null;
  return parsed as WsServerMessage;
}
